import { receiveCalls } from '../call-preferences';
import { openCallTabs, type CallTabs } from './call-tabs';
import {
  fetchCallStatus,
  outgoingEndText,
  outgoingEndTextFromServer,
  outgoingRingIsOver,
  ringIsOver,
} from './call-state';
import {
  CometChatCalls,
  type CallSession,
} from '@cometchat/calls-sdk-javascript';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CallOverlay } from './CallOverlay';
import {
  CallsContext,
  callSettings,
  type CallKind,
  type CallPeer,
  type CallPhase,
  type CallsApi,
  type JoinRequest,
} from './calls-context';
import { startTone, stopTone } from './ringtone';

/**
 * 1:1 calls with the Calls SDK's ringing flow (Ringing Flow v2), the same
 * flow the Android and iOS Connect apps use, so web <-> mobile calls ring.
 *
 * Outgoing (mirrors Android's CallSessionActivity / OutgoingCall):
 *   initiateCall(uid) creates the call, rings the receiver and prepares the
 *   caller's session (the SDK does that itself). The card says "Calling" until
 *   the callee's device prepares the call too (it does on ring), which reaches
 *   the caller as onParticipantConnected: only then does it say "Ringing". The
 *   caller sits in the room invisibly: the callee answering arrives as
 *   onParticipantJoined (roster events are emitted while prepared), the far
 *   side declining or ending as onConnectionClosed, whose reason
 *   ("Declined" / "Busy") is read from the server. Cancel =
 *   endCall('cancelled'); 60 s without an answer
 *   = endCall('unanswered').
 *
 * Incoming (web has no VoIP push):
 *   the "call initiated" message reaches the open tab over the Chat SDK's
 *   realtime connection, as a call message (onIncomingCallReceived). The
 *   ringing tab prepares the session at once with connect(), as Android and
 *   iOS do (the whole ringing flow is pre-session; only meetings join directly,
 *   owner 2026-10-15). Accept joins that prepared session; the caller ending
 *   the call through the room closes it (onConnectionClosed), which stops the
 *   ring. If the prepare fails, Accept joins directly and the ring's status is
 *   checked every few seconds instead (call-state.ts). Decline =
 *   endCall('rejected'). Already in a call: declined 'busy' without ringing.
 *   A closed tab does not ring at all.
 *
 * The provider owns ringing; Home owns the call container, so the actual join
 * is handed to Home as a JoinRequest it takes and performs.
 */

const RING_TIMEOUT_MS = 60_000; // as Android's OutgoingCall.RING_TIMEOUT_MS
const INCOMING_TIMEOUT_MS = 65_000; // a little past the caller giving up
const END_TEXT_MS = 2_000;
const LISTENER_ID = 'connect-ringing';

function peerOf(user: CometChat.User | undefined): CallPeer {
  return {
    uid: user?.getUid?.() ?? '',
    name: user?.getName?.() || user?.getUid?.() || 'Unknown',
    avatar: user?.getAvatar?.() || undefined,
  };
}

/** Lets an incoming call reach someone looking at another tab. */
/** How long Accept waits for a still-running prepare before joining directly. */
const PREPARE_WAIT_MS = 4000;

/** How often a ringing call's status is checked (see call-state.ts). */
const RING_CHECK_MS = 3000;

function notifyIncoming(peer: CallPeer, type: CallKind): Notification | null {
  if (
    !document.hidden ||
    !('Notification' in window) ||
    Notification.permission !== 'granted'
  ) {
    return null;
  }
  try {
    const n = new Notification(`Incoming ${type === 'audio' ? 'voice' : 'video'} call`, {
      body: peer.name,
      icon: peer.avatar,
      tag: 'connect-incoming-call',
      requireInteraction: true,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
    return n;
  } catch {
    return null;
  }
}

export function CallProvider({
  inCall,
  children,
}: {
  /** A meeting or call is live in Home (App's isSessionActive). */
  inCall: boolean;
  children: ReactNode;
}) {
  const [phase, setPhaseState] = useState<CallPhase>({ kind: 'idle' });
  const [joinRequest, setJoinRequest] = useState<JoinRequest | null>(null);
  const phaseRef = useRef<CallPhase>(phase);
  const inCallRef = useRef(inCall);
  inCallRef.current = inCall;
  const joinRequestRef = useRef<JoinRequest | null>(null);
  const outgoingSessionRef = useRef<CallSession | null>(null);
  // The ringing call's prepared session (the callee prepares on ring, as Android and iOS do):
  // Accept joins it, and the caller ending the call through the room closes it.
  const incomingSessionRef = useRef<CallSession | null>(null);
  const incomingPrepareRef = useRef<Promise<CallSession | null> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notificationRef = useRef<Notification | null>(null);
  const titleRef = useRef<string | null>(null);
  // Each startCall gets a number; an initiateCall resolving for an older one
  // (cancelled while still "Calling") withdraws its call instead.
  const callSeqRef = useRef(0);

  const setPhase = useCallback((next: CallPhase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const clearTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const clearAttention = () => {
    stopTone();
    notificationRef.current?.close();
    notificationRef.current = null;
    if (titleRef.current !== null) {
      document.title = titleRef.current;
      titleRef.current = null;
    }
  };

  const toIdle = useCallback(() => {
    clearTimer();
    clearAttention();
    outgoingSessionRef.current = null;
    setPhase({ kind: 'idle' });
  }, [setPhase]);

  /** Lets go of the ringing call's prepared session (not answered here). */
  const releaseIncoming = useCallback((leave = true) => {
    const session = incomingSessionRef.current;
    incomingSessionRef.current = null;
    incomingPrepareRef.current = null;
    if (leave && session) void session.leave();
  }, []);

  // Other Connect tabs (call-tabs.ts): answered or declined in one = stop ringing in all.
  const tabsRef = useRef<CallTabs | null>(null);
  useEffect(() => {
    const tabs = openCallTabs(sessionId => {
      const cur = phaseRef.current;
      if (cur.kind === 'incoming' && cur.sessionId === sessionId) {
        releaseIncoming();
        toIdle();
      }
    });
    tabsRef.current = tabs;
    return () => {
      tabs.close();
      tabsRef.current = null;
    };
  }, [toIdle, releaseIncoming]);
  useEffect(() => {
    tabsRef.current?.setInCall(inCall);
  }, [inCall]);

  // While a call rings here, check every few seconds that it still is (call-state.ts): an end
  // announced through the caller's room never reaches this tab as a "cancelled" call message.
  // This runs whether or not the ring is prepared: a prepared ring usually hears the end as
  // onConnectionClosed, but that only fires if the server destroys the room, so the poll stays
  // on as the backstop rather than leaving the tab ringing to its own timer.
  const ringingSessionId = phase.kind === 'incoming' ? phase.sessionId : null;
  const ringingIsGroup = phase.kind === 'incoming' && !!phase.groupName;
  useEffect(() => {
    if (!ringingSessionId) return;
    let stopped = false;
    const check = async () => {
      const status = await fetchCallStatus(ringingSessionId);
      if (stopped) return;
      const cur = phaseRef.current;
      if (cur.kind === 'incoming' && cur.sessionId === ringingSessionId && ringIsOver(status, ringingIsGroup)) {
        releaseIncoming();
        toIdle();
      }
    };
    const timer = setInterval(() => void check(), RING_CHECK_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [ringingSessionId, ringingIsGroup, toIdle, releaseIncoming]);

  const requestJoin = useCallback(
    (request: JoinRequest) => {
      clearTimer();
      clearAttention();
      joinRequestRef.current = request;
      setJoinRequest(request);
      setPhase({ kind: 'idle' });
    },
    [setPhase],
  );

  const endOutgoing = useCallback(
    (endText: string) => {
      const cur = phaseRef.current;
      if (cur.kind !== 'outgoing') return;
      clearTimer();
      clearAttention();
      outgoingSessionRef.current = null;
      setPhase({ ...cur, status: 'ended', endText });
      timerRef.current = setTimeout(() => {
        if (phaseRef.current.kind === 'outgoing') setPhase({ kind: 'idle' });
      }, END_TEXT_MS);
    },
    [setPhase],
  );

  // The same check for our own outgoing call. The callee's decline reaches the server
  // (it records `rejected` / `busy` at once) but nothing pushes it to the caller: the
  // room announce is never delivered here and no call message arrives, so without this
  // the caller rang on to its own 60 s timer and then said "No answer" (ENG-40329).
  //
  // Polled from the moment the call exists (it has a session ID), through "Calling" as well as
  // "Ringing". "Ringing" waits for the callee's device to prepare the session, and a callee that
  // declines from the notification before its prepare connects never gets there; polling only
  // while "Ringing" left the caller on "Calling…" until "No answer". Android and iOS poll from
  // initiateCall the same way.
  const outgoingRingingSessionId =
    phase.kind === 'outgoing' && (phase.status === 'calling' || phase.status === 'ringing')
      ? (phase.sessionId ?? null)
      : null;
  useEffect(() => {
    if (!outgoingRingingSessionId) return;
    let stopped = false;
    const check = async () => {
      const status = await fetchCallStatus(outgoingRingingSessionId);
      if (stopped) return;
      const endText = outgoingRingIsOver(status);
      if (!endText) return; // still ringing, or the status could not be read
      const cur = phaseRef.current;
      if (
        cur.kind === 'outgoing' &&
        (cur.status === 'calling' || cur.status === 'ringing') &&
        cur.sessionId === outgoingRingingSessionId
      ) {
        endOutgoing(endText);
      }
    };
    const timer = setInterval(() => void check(), RING_CHECK_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [outgoingRingingSessionId, endOutgoing]);


  // ---- outgoing --------------------------------------------------------

  const startCall = useCallback(
    async (peer: CallPeer, type: CallKind) => {
      if (phaseRef.current.kind !== 'idle' || inCallRef.current) return;
      // A user gesture: the one moment the browser lets us ask, so incoming
      // calls can notify while this tab is in the background later.
      if ('Notification' in window && Notification.permission === 'default') {
        void Notification.requestPermission();
      }
      const seq = ++callSeqRef.current;
      setPhase({ kind: 'outgoing', peer, type, status: 'calling' });
      startTone('ringback');

      const isGroup = peer.kind === 'group';
      const { data, error } = await CometChatCalls.initiateCall(peer.uid, {
        type,
        receiverType: isGroup ? 'group' : 'user',
        sessionSettings: callSettings(type, isGroup ? peer.name : `Call with ${peer.name}`, !isGroup),
      });

      // Read afresh: the call may have been cancelled during the await.
      const cur = phaseRef.current as CallPhase;
      const stillOurs =
        seq === callSeqRef.current &&
        cur.kind === 'outgoing' &&
        cur.status === 'calling';
      if (!stillOurs) {
        // Cancelled while "Calling": the call exists now, so withdraw it.
        if (data) void CometChatCalls.endCall(data.sessionId, 'cancelled');
        return;
      }
      if (error || !data) {
        console.warn('initiateCall failed:', error);
        endOutgoing("Couldn't place the call");
        return;
      }
      outgoingSessionRef.current = data.session;
      // Still "Calling": it rings once the callee's device prepares it (onParticipantConnected).
      setPhase({ ...cur, sessionId: data.sessionId });
      timerRef.current = setTimeout(() => {
        // A group the caller stops waiting on is withdrawn ('cancelled'):
        // "unanswered" is a per-member outcome (as Android's OutgoingCall).
        void CometChatCalls.endCall(data.sessionId, isGroup ? 'cancelled' : 'unanswered');
        endOutgoing('No answer');
      }, RING_TIMEOUT_MS);
    },
    [setPhase, endOutgoing],
  );

  const cancel = useCallback(() => {
    const cur = phaseRef.current;
    if (cur.kind !== 'outgoing') return;
    if (cur.status !== 'ended' && cur.sessionId) {
      void CometChatCalls.endCall(cur.sessionId, 'cancelled');
    }
    callSeqRef.current += 1; // an initiateCall still in flight withdraws itself
    toIdle();
  }, [toIdle]);

  // The callee's device preparing the call (it rings), the callee answering,
  // or the far side ending the call, shows up on the caller's prepared session
  // as roster / connection events.
  useEffect(() => {
    const offConnected = CometChatCalls.addEventListener('onParticipantConnected', participant => {
      const cur = phaseRef.current;
      if (cur.kind !== 'outgoing' || cur.status !== 'calling' || !cur.sessionId) {
        return;
      }
      // Not one of our own devices in the room: only the callee's ringing counts.
      if (participant.uid === CometChatCalls.getLoggedInUser()?.uid) return;
      setPhase({ ...cur, status: 'ringing' });
    });
    const offJoined = CometChatCalls.addEventListener('onParticipantJoined', () => {
      const cur = phaseRef.current;
      // Also while "Calling": a callee whose prepare failed answers with a direct join.
      if (cur.kind !== 'outgoing' || cur.status === 'ended' || !cur.sessionId) {
        return;
      }
      requestJoin({
        sessionId: cur.sessionId,
        type: cur.type,
        title: `Call with ${cur.peer.name}`,
        isPeerCall: cur.peer.kind !== 'group',
        session: outgoingSessionRef.current ?? undefined,
      });
    });
    const offClosed = CometChatCalls.addEventListener('onConnectionClosed', () => {
      const cur = phaseRef.current;
      if (cur.kind === 'outgoing' && cur.status !== 'ended' && cur.sessionId) {
        // "Calling" or "Ringing". The close carries no reason: ask the server why the call
        // ended before saying "Call ended" (a cancel/answer meanwhile wins).
        const { sessionId } = cur;
        void outgoingEndTextFromServer(sessionId).then(text => {
          const now = phaseRef.current;
          if (now.kind === 'outgoing' && now.status !== 'ended' && now.sessionId === sessionId) {
            endOutgoing(text);
          }
        });
      } else if (cur.kind === 'incoming' && incomingSessionRef.current) {
        // The ringing call's prepared session closed: the caller cancelled (through the room)
        // or the call ended. Stop ringing at once, no status check needed.
        releaseIncoming(false);
        toIdle();
      }
    });
    return () => {
      offConnected();
      offJoined();
      offClosed();
    };
  }, [setPhase, requestJoin, endOutgoing, releaseIncoming, toIdle]);

  // ---- incoming --------------------------------------------------------

  useEffect(() => {
    CometChat.addCallListener(
      LISTENER_ID,
      new CometChat.CallListener({
        onIncomingCallReceived: (call: CometChat.Call) => {
          const isGroup =
            call.getReceiverType() === CometChat.RECEIVER_TYPE.GROUP;
          const caller =
            (call.getCallInitiator?.() as CometChat.User | undefined) ??
            (call.getSender() as CometChat.User);
          // The Chat SDK also hands the CALLER their own call message as
          // "incoming" (here, and on their other devices). Treating it as a
          // call to us would see this tab busy placing it and decline it
          // 'busy', which ended every outgoing call before the other side
          // could ring.
          const me = CometChatCalls.getLoggedInUser()?.uid;
          if (me && caller?.getUid?.() === me) return;
          // Profile → Receive calls is off for this browser: don't ring, and don't decline
          // either, so the user's other devices still can.
          if (!receiveCalls()) return;
          const sessionId = call.getSessionId();
          const initiatedAt = call.getInitiatedAt?.() ?? 0;
          // A message delivered late (tab asleep, reconnect) for a call that
          // has long stopped ringing.
          if (initiatedAt && Date.now() / 1000 - initiatedAt > 60) return;
          // On a call in another Connect tab: that tab tells a 1:1 caller we're busy.
          if (tabsRef.current?.otherTabInCall()) return;
          if (phaseRef.current.kind !== 'idle' || inCallRef.current) {
            // A 1:1 call is told we're busy. A group call is just not shown:
            // one member being busy must not end it for the others.
            if (!isGroup) void CometChatCalls.endCall(sessionId, 'busy');
            return;
          }
          const peer = peerOf(caller);
          const type: CallKind = call.getType() === 'audio' ? 'audio' : 'video';
          const receiver = call.getReceiver() as { getName?: () => string } | undefined;
          const groupName = isGroup ? receiver?.getName?.() || 'Group call' : undefined;
          setPhase({ kind: 'incoming', peer, type, sessionId, groupName });
          startTone('incoming');
          notificationRef.current = notifyIncoming(peer, type);
          titleRef.current = document.title;
          document.title = `Incoming call · ${peer.name}`;
          timerRef.current = setTimeout(() => {
            releaseIncoming();
            toIdle();
          }, INCOMING_TIMEOUT_MS);
          // Prepare while it rings (as Android and iOS): Accept then only has to join, and the
          // caller cancelling through the room reaches us as onConnectionClosed.
          const title = groupName ?? `Call with ${peer.name}`;
          const prepare = CometChatCalls.connect(sessionId, callSettings(type, title, !isGroup))
            .then(session => {
              const cur = phaseRef.current;
              const stillRinging = cur.kind === 'incoming' && cur.sessionId === sessionId;
              if (incomingPrepareRef.current !== prepare) {
                // Declined / cancelled / answered elsewhere meanwhile: nobody will join it.
                if (!stillRinging && session.prepareStatus === 'prepared') void session.leave();
                return null;
              }
              if (session.prepareStatus !== 'prepared') return null;
              incomingSessionRef.current = session;
              return session;
            })
            .catch(error => {
              // Accept falls back to a direct join; the status check keeps watching the ring.
              console.warn('Preparing the incoming call failed:', error);
              return null;
            });
          incomingPrepareRef.current = prepare;
        },
        onIncomingCallCancelled: (call: CometChat.Call) => {
          const cur = phaseRef.current;
          if (cur.kind === 'incoming' && cur.sessionId === call.getSessionId()) {
            releaseIncoming();
            toIdle();
          }
        },
        // The callee declined. This is the only path that reaches a ringing
        // caller when the callee was never in the room — a phone declining
        // from its notification before its pre-warm finished. That decline
        // goes out as `PUT /calls/:id`, which the server turns into this call
        // message; the room is never destroyed, so `onConnectionClosed` does
        // not fire and the caller used to ring on to its own 60 s timer and
        // then say "No answer" (ENG-40329).
        onOutgoingCallRejected: (call: CometChat.Call) => {
          const cur = phaseRef.current;
          if (
            cur.kind !== 'outgoing' ||
            cur.status !== 'ringing' ||
            cur.sessionId !== call.getSessionId()
          ) {
            return;
          }
          endOutgoing(outgoingEndText(call.getStatus()) ?? 'Call ended');
        },
      }),
    );
    return () => CometChat.removeCallListener(LISTENER_ID);
  }, [setPhase, toIdle, releaseIncoming, endOutgoing]);

  const accept = useCallback(async () => {
    const cur = phaseRef.current;
    if (cur.kind !== 'incoming') return;
    tabsRef.current?.handled(cur.sessionId);
    // Join the prepared session; if the prepare is still running, give it a moment.
    const pending = incomingPrepareRef.current;
    const session =
      incomingSessionRef.current ??
      (pending
        ? await Promise.race([pending, new Promise<null>(r => setTimeout(() => r(null), PREPARE_WAIT_MS))])
        : null);
    // Handed over: the join owns it now, so it must not be released.
    incomingSessionRef.current = null;
    incomingPrepareRef.current = null;
    requestJoin({
      sessionId: cur.sessionId,
      type: cur.type,
      title: cur.groupName ?? `Call with ${cur.peer.name}`,
      isPeerCall: !cur.groupName,
      session: session ?? undefined,
    });
  }, [requestJoin]);

  const decline = useCallback(() => {
    const cur = phaseRef.current;
    if (cur.kind !== 'incoming') return;
    tabsRef.current?.handled(cur.sessionId);
    // Declining a group call stays local: the call goes on for the others, so only our prepared
    // session goes. A 1:1 decline is endCall('rejected'), which also leaves the prepared session.
    if (cur.groupName) {
      releaseIncoming();
    } else {
      releaseIncoming(false);
      void CometChatCalls.endCall(cur.sessionId, 'rejected');
    }
    toIdle();
  }, [toIdle, releaseIncoming]);

  const takeJoinRequest = useCallback(() => {
    const request = joinRequestRef.current;
    joinRequestRef.current = null;
    setJoinRequest(null);
    return request;
  }, []);

  // Leaving the signed-in app mid-ring (sign out) must not leave tones going.
  useEffect(() => () => clearAttention(), []);

  const api = useMemo<CallsApi>(
    () => ({
      phase,
      busy: phase.kind !== 'idle' || inCall || joinRequest !== null,
      joinRequest,
      startCall: (peer, type) => void startCall(peer, type),
      cancel,
      accept: () => void accept(),
      decline,
      takeJoinRequest,
    }),
    [phase, inCall, joinRequest, startCall, cancel, accept, decline, takeJoinRequest],
  );

  return (
    <CallsContext.Provider value={api}>
      {children}
      <CallOverlay phase={phase} onCancel={cancel} onAccept={accept} onDecline={decline} />
    </CallsContext.Provider>
  );
}
